import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "../../lib/api";

const dayKey = (iso, tz) => new Date(iso).toLocaleDateString("en-CA", { timeZone: tz });

export default function SlotPicker({ tz, selected, onSelect }) {
  const [slots, setSlots] = useState(null);
  const [day, setDay] = useState(null);

  useEffect(() => { api.get("/slots").then(r => setSlots(r.data)).catch(() => setSlots([])); }, []);

  const byDay = useMemo(() => {
    const m = {};
    (slots || []).forEach(s => { const k = dayKey(s.start_utc, tz); (m[k] = m[k] || []).push(s); });
    return m;
  }, [slots, tz]);
  const days = Object.keys(byDay).sort();
  const activeDay = day || days.find(d => byDay[d].some(s => s.status === "open"));

  if (!slots) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-teal-700" /></div>;
  if (!days.length) return <p className="text-slate-600" data-testid="no-slots">No slots are open right now. Please check back soon or contact us on WhatsApp.</p>;

  return (
    <div>
      <p className="text-sm text-slate-600 mb-5">Times shown in your timezone: <span className="font-medium text-slate-900" data-testid="detected-tz">{tz}</span></p>
      <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1">
        {days.map(d => {
          const dt = new Date(d + "T12:00:00");
          const open = byDay[d].filter(s => s.status === "open").length;
          return (
            <button key={d} type="button" disabled={!open} onClick={() => setDay(d)} data-testid={`day-${d}`}
              className={`shrink-0 w-20 rounded-xl border py-3 text-center transition-colors disabled:opacity-40 ${activeDay === d ? "border-teal-700 bg-teal-700 text-white" : "border-slate-200 bg-white hover:border-teal-600"}`}>
              <div className="text-xs uppercase">{dt.toLocaleDateString("en-IN", { weekday: "short" })}</div>
              <div className="text-xl font-serif">{dt.getDate()}</div>
              <div className="text-[11px]">{dt.toLocaleDateString("en-IN", { month: "short" })}</div>
            </button>
          );
        })}
      </div>
      <div className="mt-6 grid grid-cols-3 sm:grid-cols-5 gap-3">
        {(byDay[activeDay] || []).map(s => {
          const label = new Date(s.start_utc).toLocaleTimeString("en-IN", { timeZone: tz, hour: "2-digit", minute: "2-digit" });
          const open = s.status === "open";
          const sel = selected?.id === s.id;
          return (
            <button key={s.id} type="button" disabled={!open} onClick={() => onSelect(s)} data-testid={`slot-${s.ist_date}-${s.ist_time.replace(":", "")}`}
              className={`rounded-lg border py-3 text-sm font-medium transition-colors ${sel ? "bg-teal-700 border-teal-700 text-white" : open ? "bg-white border-slate-200 text-slate-800 hover:border-teal-600" : "bg-slate-100 border-slate-100 text-slate-400 line-through cursor-not-allowed"}`}>
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
